import {
    DS_COLOR,
    DS_HEIGHT,
    DS_SHAPE_BORDER_RADIUS,
} from '@calm/evexia-design-tokens';
import useAuthRedirect from '@rhg/hooks/use-auth-redirect';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useProvidedTheme } from '@rhg/hooks/use-provided-theme';
import landingMessages from '@rhg/lang/messages/landing';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    AppBar,
    Box,
    SxProps,
    Toolbar,
    Theme,
    Button,
    alpha,
    ThemeProvider,
} from '@rhg/libs/material';
import { styleColorOverlay, sxAppend } from '@rhg/libs/material/sx';
import { routes } from '@rhg/utils/routes';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import AppLogo from '../app-logo';
import AuthenticatedComponent from '../authenticated-component';
import CHLink from '../ch-link';
import MobileMainMenu from '../mobile-main-menu';
import NavItems from '../nav-items';
import { QrCodeBanner } from '../qr-code-banner';
import RouteRestrictedComponent from '../route-restricted-component';

const styleButtonBox: SxProps<Theme> = {
    display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
    flex: 1,
    justifyContent: 'flex-start',
};
const styleLoginBox: SxProps<Theme> = {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
    gap: 4,
};
const styleAppAndBrand: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
};
const styleAppBarBackground = {
    background: ({ palette: { mode } }: Theme) =>
        mode === 'light'
            ? DS_COLOR.WHITE_100P
            : styleColorOverlay(
                  DS_COLOR.DARK_BACKGROUND_PRIMARY_START,
                  DS_COLOR.BLACK_20P
              ).background,
};
const styleAppBar = {
    ...styleAppBarBackground,
};
const styleToolbar = {
    ...styleAppBarBackground,
};
const styleToolbarItems: SxProps<Theme> = {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    width: '100%',
};
const styleToolbarButton: SxProps<Theme> = {
    height: DS_HEIGHT.TOOLBAR,
    minWidth: DS_HEIGHT.TOOLBAR,
    color: 'text.sub',
    py: 3,
    px: 4,
    top: '1px', // Gets border bottom to overlay the toolbar
    '&[aria-current="page"]': {
        borderBottom: 3,
        borderColor: 'primary.main',
        color: 'primary.main',
        pb: (theme: Theme) => `calc(${theme.spacing(3)} - 3px)`, // Makes sure the text is in the same place
        opacity: 1,
    },
};

const styleMobileMenuButton: SxProps<Theme> = {
    justifyContent: 'flex-start',
    color: 'text.sub',
    borderRadius: `${DS_SHAPE_BORDER_RADIUS.MEDIUM}px !important`,
    pl: 4,
    '&.Mui-disabled': {
        color: 'text.body',
    },
};

const styleSeeThruAppBar = {
    backgroundColor: 'transparent',
    borderRadius: 0,
    backgroundImage: 'none',
    border: 0,
};
const styleSeeThruToolbar = {
    backgroundColor: 'transparent',
    border: 0,
};

const styleBarShadow = {
    background: ({ palette: { mode } }: Theme) =>
        mode === 'light'
            ? 'none'
            : `linear-gradient(0deg, ${alpha(
                  DS_COLOR.LIGHT_TEXT_BODY,
                  0
              )}, ${alpha(DS_COLOR.LIGHT_TEXT_BODY, 0.42)})`,
    backgroundImage: 'invalid-value !important', // needed to override default value
};

const drawerTestId = 'DRAWER';
const toolbarTestId = 'TOOLBAR';

interface HeaderProps {
    isSeeThru?: boolean;
    isSSO?: boolean;
}

interface EmptyHeaderProps {
    isClickable?: boolean;
}
export function EmptyHeader({ isClickable = false }: EmptyHeaderProps) {
    return (
        <AppBar sx={styleAppBar}>
            <Toolbar sx={styleToolbar}>
                <Box sx={styleAppAndBrand}>
                    <AppLogo clickable={isClickable} />
                </Box>
            </Toolbar>
        </AppBar>
    );
}

const Header = ({ isSeeThru, isSSO }: HeaderProps) => {
    const isMobile = useIsMobile('lg');
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const { pathname } = useRouter();
    const searchParams = useSearchParams();
    const { createRedirectQueryString } = useAuthRedirect();

    const redirectQuery = `?${createRedirectQueryString(
        isSSO ? `${routes.dashboard}?${searchParams.toString()}` : undefined
    )}`;

    const signupHref = `${routes.signup}${redirectQuery}`;
    const loginHref = `${routes.login}${redirectQuery}`;

    const handleDrawerToggle = () => {
        setDrawerOpen(!isDrawerOpen);
    };
    const { formatMessage } = useIntl();
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

    const isSignInButtonDisplayed = useMemo(
        () =>
            pathname === routes.dashboard && !isAuthLoading && !isAuthenticated,
        [isAuthLoading, isAuthenticated, pathname]
    );

    const lightTheme = useProvidedTheme('light');

    const hasSeeThruStyle = useMemo(
        () => isSeeThru && (isSSO || !isAuthenticated),
        // TODO: fix this eslint-disable
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isAuthenticated, isSeeThru]
    );

    return (
        <AppBar
            data-testid="HEADER"
            sx={sxAppend(
                styleAppBar,
                hasSeeThruStyle && styleSeeThruAppBar,
                styleBarShadow
            )}
        >
            <Toolbar sx={hasSeeThruStyle ? styleSeeThruToolbar : styleToolbar}>
                <Box sx={styleAppAndBrand}>
                    <AppLogo
                        clickable={
                            pathname !== routes.registration &&
                            pathname !== routes.sso &&
                            pathname !== routes.ssoInfo
                        }
                    />
                </Box>
                {isSignInButtonDisplayed && (
                    <Box sx={styleLoginBox}>
                        <Button
                            trackButton="CreateCalmAccount"
                            variant="text"
                            size="large"
                            component={CHLink}
                            href={signupHref}
                        >
                            {formatMessage(landingMessages['landing.sign_up'])}
                        </Button>
                        <ThemeProvider theme={lightTheme}>
                            <Button
                                trackButton="LoginCalmAccount"
                                variant="contained"
                                size="large"
                                component={CHLink}
                                href={loginHref}
                            >
                                {formatMessage(
                                    landingMessages['landing.log_in']
                                )}
                            </Button>
                        </ThemeProvider>
                    </Box>
                )}
                <RouteRestrictedComponent
                    disallow={[routes.auth, routes.registration]}
                >
                    <>
                        {!isSSO && (
                            <AuthenticatedComponent requireAccount>
                                <MobileMainMenu
                                    isOpen={isDrawerOpen}
                                    handleToggle={handleDrawerToggle}
                                >
                                    <NavItems
                                        size="large"
                                        testId={drawerTestId}
                                        sx={styleMobileMenuButton}
                                        onClick={handleDrawerToggle}
                                    />
                                </MobileMainMenu>
                            </AuthenticatedComponent>
                        )}
                        {!isSSO && (
                            <AuthenticatedComponent requireAccount>
                                <Box sx={styleButtonBox} role="navigation">
                                    <Box sx={styleToolbarItems}>
                                        <NavItems
                                            sx={styleToolbarButton}
                                            testId={toolbarTestId}
                                            addSyntheticIds
                                        />
                                    </Box>
                                </Box>
                            </AuthenticatedComponent>
                        )}
                        {!isAuthLoading && !isAuthenticated && (
                            <RouteRestrictedComponent
                                disallow={[routes.dashboard]}
                            >
                                <Box sx={styleLoginBox} role="navigation">
                                    <Button
                                        trackButton="CreateCalmAccount"
                                        variant="text"
                                        size="large"
                                        component={CHLink}
                                        href={signupHref}
                                    >
                                        {formatMessage(
                                            landingMessages['landing.sign_up']
                                        )}
                                    </Button>
                                    <ThemeProvider theme={lightTheme}>
                                        <Button
                                            trackButton="LoginCalmAccount"
                                            variant="contained"
                                            size="large"
                                            component={CHLink}
                                            href={loginHref}
                                        >
                                            {formatMessage(
                                                landingMessages[
                                                    'landing.log_in'
                                                ]
                                            )}
                                        </Button>
                                    </ThemeProvider>
                                </Box>
                            </RouteRestrictedComponent>
                        )}
                    </>
                </RouteRestrictedComponent>
                {!isAuthLoading && isAuthenticated && !isMobile && (
                    <QrCodeBanner />
                )}
            </Toolbar>
        </AppBar>
    );
};

export default Header;
